import React, { useState, useEffect, useCallback, useMemo } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  PlaceModel,
  ShareModel,
  IDate,
  ITagAssociationMeta,
  IEntityMemoryMeta,
  IEntityMediaMeta,
  IEntityContactMeta,
  IEntityGroupMeta,
  IEntityPlaceMeta,
  IEntityAnchorMeta,
  formatDisplayDate,
} from "common247";
import { ThemedText } from "../ThemedText";
import { ThemedInput } from "../ThemedInput";
import { ThemedButton } from "../ThemedButton";
import { ThemedView } from "../ThemedView";
import { ThemedRating } from "../ThemedRating";
import { SharingControl } from "../sharing/SharingControl";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useActiveFieldTracker } from "@/hooks/useActiveField";
import { AutoSaveIndicator } from "@/components/ui/AutoSaveIndicator";
import { DateSelector } from "@/components/date/DateSelector";
import { TagListStrip } from "../tags/TagListStrip";
import { EntityLinkStrip } from "../ui/EntityLinkStrip";
import { CollaborativeEditor } from "../editor/CollaborativeEditor";
import { GoogleMapsLocationSelector } from "../location/GoogleMapsLocationSelector";
import { useFirebase } from "../firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { CustomAlert } from "@/components/ThemedAlert";

/**
 * Props for the PlaceEditor component.
 */
export interface IPlaceEditorProps {
  /** Existing place model if editing */
  place?: PlaceModel;
  /** Existing shares */
  shares?: ShareModel[];
  /** Loading shares flag */
  isLoadingShares?: boolean;
  /** Current authenticated user ID */
  currentUserId?: string;
  /** Save callback */
  onSave: (data: {
    name: string;
    description?: string;
    date?: IDate;
    address?: string;
    latitude?: number;
    longitude?: number;
    favouriteRating?: number;
    photoMediaId?: string;
    tags?: Record<string, ITagAssociationMeta>;
    memories?: Record<string, IEntityMemoryMeta>;
    media?: Record<string, IEntityMediaMeta>;
    contacts?: Record<string, IEntityContactMeta>;
    groups?: Record<string, IEntityGroupMeta>;
    places?: Record<string, IEntityPlaceMeta>;
    anchors?: Record<string, IEntityAnchorMeta>;
  }) => Promise<void> | void;
  /** Share callback */
  onShare?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  /** Unshare callback */
  onUnshare?: (shareId: string) => void;
  /** Cancel callback */
  onCancel: () => void;
  /** Delete callback */
  onDelete?: () => Promise<void> | void;
  /** Saving in-flight flag */
  isSaving: boolean;
  /** Submit button text */
  submitTitle?: string;
  /** Optional child elements */
  children?: React.ReactNode;
}

/**
 * First-class PlaceEditor component for creating and modifying Places.
 * Standardizes:
 * - Name input (fail-fast validation)
 * - Address & Coordinates with GoogleMapsLocationSelector
 * - Structured Date with DateSelector
 * - Rich description with CollaborativeEditor
 * - Rating with ThemedRating
 * - Cross-entity linking (Memories, Media, Contacts, Groups)
 * - Tagging with TagListStrip
 * - Sharing control with SharingControl
 * - Auto-save integration with AutoSaveIndicator
 */
export function PlaceEditor({
  place,
  shares = [],
  isLoadingShares,
  currentUserId,
  onSave,
  onShare,
  onUnshare,
  onCancel,
  onDelete,
  isSaving,
  submitTitle = "Save Place",
  children,
}: IPlaceEditorProps) {
  const [formName, setFormName] = useState(place?.name || "");
  const [formDesc, setFormDesc] = useState(place?.description || "");
  const [formDate, setFormDate] = useState<IDate | undefined>(place?.date);
  const [formAddress, setFormAddress] = useState(place?.address || "");
  const [formLat, setFormLat] = useState<number | undefined>(place?.latitude);
  const [formLng, setFormLng] = useState<number | undefined>(place?.longitude);
  const [favouriteRating, setFavouriteRating] = useState<number>(place?.favouriteRating ?? 3);
  const [nameError, setNameError] = useState("");

  // Modals
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // Cross-entity links
  const [memoriesMap, setMemoriesMap] = useState<Record<string, IEntityMemoryMeta>>(place?.memories || {});
  const [mediaMap, setMediaMap] = useState<Record<string, IEntityMediaMeta>>(place?.media || {});
  const [contactsMap, setContactsMap] = useState<Record<string, IEntityContactMeta>>(place?.contacts || {});
  const [groupsMap, setGroupsMap] = useState<Record<string, IEntityGroupMeta>>(place?.groups || {});
  const [placesMap, setPlacesMap] = useState<Record<string, IEntityPlaceMeta>>(place?.places || {});
  const [anchorsMap, setAnchorsMap] = useState<Record<string, IEntityAnchorMeta>>(place?.anchors || {});
  const [tagsMap, setTagsMap] = useState<Record<string, ITagAssociationMeta>>(place?.tags || {});

  const services = useFirebase();
  const resolvedUserId = currentUserId || services?.auth?.currentUser?.uid || "";
  const userRole = place?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";

  const { activeField, setActiveField, isFieldActive } = useActiveFieldTracker();

  // Reset fields if place changes
  useEffect(() => {
    if (place) {
      setFormName(place.name || "");
      setFormDesc(place.description || "");
      setFormDate(place.date);
      setFormAddress(place.address || "");
      setFormLat(place.latitude);
      setFormLng(place.longitude);
      setFavouriteRating(place.favouriteRating ?? 3);
      setMemoriesMap(place.memories || {});
      setMediaMap(place.media || {});
      setContactsMap(place.contacts || {});
      setGroupsMap(place.groups || {});
      setPlacesMap(place.places || {});
      setAnchorsMap(place.anchors || {});
      setTagsMap(place.tags || {});
    }
  }, [place]);

  const handleManualSave = useCallback(async () => {
    const trimmedName = (formName || "").trim();
    if (!trimmedName) {
      setNameError("Place name is required.");
      return;
    }
    setNameError("");

    await onSave({
      name: trimmedName,
      description: (formDesc || "").trim() || undefined,
      date: formDate,
      address: (formAddress || "").trim() || undefined,
      latitude: formLat,
      longitude: formLng,
      favouriteRating,
      tags: tagsMap,
      memories: memoriesMap,
      media: mediaMap,
      contacts: contactsMap,
      groups: groupsMap,
      places: placesMap,
      anchors: anchorsMap,
    });
  }, [
    formName,
    formDesc,
    formDate,
    formAddress,
    formLat,
    formLng,
    favouriteRating,
    tagsMap,
    memoriesMap,
    mediaMap,
    contactsMap,
    groupsMap,
    placesMap,
    anchorsMap,
    onSave,
  ]);

  // Auto-save setup
  const currentFormState = useMemo(
    () => ({
      name: formName,
      description: formDesc,
      date: formDate,
      address: formAddress,
      latitude: formLat,
      longitude: formLng,
      favouriteRating,
      tags: tagsMap,
      memories: memoriesMap,
      media: mediaMap,
      contacts: contactsMap,
      groups: groupsMap,
      places: placesMap,
      anchors: anchorsMap,
    }),
    [
      formName,
      formDesc,
      formDate,
      formAddress,
      formLat,
      formLng,
      favouriteRating,
      tagsMap,
      memoriesMap,
      mediaMap,
      contactsMap,
      groupsMap,
      placesMap,
      anchorsMap,
    ]
  );

  const isExisting = !!place?.id;

  const autoSave = useAutoSave({
    data: currentFormState,
    enabled: isExisting && canEdit && !!formName.trim(),
    debounceMs: 600,
    onSave: async (state) => {
      const trimmedName = (state.name || "").trim();
      if (!place?.id || !trimmedName) return;
      await onSave({
        name: trimmedName,
        description: (state.description || "").trim() || undefined,
        date: state.date,
        address: (state.address || "").trim() || undefined,
        latitude: state.latitude,
        longitude: state.longitude,
        favouriteRating: state.favouriteRating,
        tags: state.tags,
        memories: state.memories,
        media: state.media,
        contacts: state.contacts,
        groups: state.groups,
        places: state.places,
      });
    },
  });

  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({ light: "#64748B", dark: "#94A3B8" }, "text");

  return (
    <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.contentContainer}>
      {/* Details Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>
          Place Details
        </ThemedText>

        <View style={styles.fieldBlock}>
          <ThemedText type="fieldLabel">Place Name *</ThemedText>
          <ThemedInput
            value={formName}
            onChangeText={(val) => {
              setFormName(val);
              if (nameError) setNameError("");
            }}
            placeholder="e.g. Central Park, Eiffel Tower, Home"
            error={nameError}
            onFocus={() => setActiveField("name")}
            onBlur={() => setActiveField(null)}
            editable={canEdit}
            testID="place-name-input"
          />
        </View>

        {/* Address and Map Coordinates */}
        <View style={styles.fieldBlock}>
          <ThemedText type="fieldLabel">Address</ThemedText>
          <View style={styles.addressRow}>
            <View style={{ flex: 1 }}>
              <ThemedInput
                value={formAddress}
                onChangeText={setFormAddress}
                placeholder="e.g. 100 Main St, New York, NY"
                onFocus={() => setActiveField("address")}
                onBlur={() => setActiveField(null)}
                editable={canEdit}
                testID="place-address-input"
              />
            </View>

            <TouchableOpacity
              style={[styles.mapPickerButton, { borderColor: tintColor }]}
              onPress={() => setShowLocationPicker(true)}
              accessibilityLabel="Pick location on map"
            >
              <Ionicons name="map-outline" size={18} color={tintColor} />
              <ThemedText style={[styles.mapPickerText, { color: tintColor }]}>
                Map
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>

        {formLat !== undefined && formLng !== undefined && (
          <ThemedText style={[styles.coordinatesBadge, { color: textMuted }]}>
            Coordinates: {formLat.toFixed(4)}, {formLng.toFixed(4)}
          </ThemedText>
        )}

        {/* Structured Date */}
        <View style={styles.fieldBlock}>
          <ThemedText type="fieldLabel">Date</ThemedText>
          <DateSelector
            date={formDate}
            onChangeDate={setFormDate}
            editable={canEdit}
          />
        </View>

        {/* Rating */}
        <View style={styles.ratingRow}>
          <ThemedText type="fieldLabel">Favourite Rating</ThemedText>
          <ThemedRating
            value={favouriteRating}
            onChange={setFavouriteRating}
            readOnly={!canEdit}
            size={18}
            showLabel={false}
            style={styles.ratingControl}
          />
        </View>
      </ThemedView>

      {/* Description Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>
          Description
        </ThemedText>
        <CollaborativeEditor
          entityId={place?.id}
          entityType="place"
          value={formDesc}
          onChange={setFormDesc}
          placeholder="Add stories, historical notes, or details about this place... Use @ for contacts/groups, # for memories, and + for media."
          editable={canEdit}
        />
      </ThemedView>

      {/* 1. Linked Contacts Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Contacts</ThemedText>
        <EntityLinkStrip
          entityId={place?.id}
          entityType="place"
          targetType="contact"
          links={contactsMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setContactsMap}
        />
      </ThemedView>

      {/* 2. Linked Groups Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Groups</ThemedText>
        <EntityLinkStrip
          entityId={place?.id}
          entityType="place"
          targetType="group"
          links={groupsMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setGroupsMap}
        />
      </ThemedView>

      {/* 3. Linked Places Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Places</ThemedText>
        <EntityLinkStrip
          entityId={place?.id}
          entityType="place"
          targetType="place"
          links={placesMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setPlacesMap}
        />
      </ThemedView>

      {/* 4. Linked Anchors Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Anchors</ThemedText>
        <EntityLinkStrip
          entityId={place?.id}
          entityType="place"
          targetType="anchor"
          links={anchorsMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setAnchorsMap}
        />
      </ThemedView>

      {/* 5. Linked Media Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Media</ThemedText>
        <EntityLinkStrip
          entityId={place?.id}
          entityType="place"
          targetType="media"
          links={mediaMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setMediaMap}
        />
      </ThemedView>

      {/* 6. Linked Memories Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Memories</ThemedText>
        <EntityLinkStrip
          entityId={place?.id}
          entityType="place"
          targetType="memory"
          links={memoriesMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setMemoriesMap}
        />
      </ThemedView>

      {/* 7. Tags Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Tags & Categories</ThemedText>
        <TagListStrip
          entityId={place?.id}
          entityType="place"
          tags={tagsMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onTagsChange={setTagsMap}
        />
      </ThemedView>

      {/* 8. Sharing Control */}
      {place?.id && (
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Shares</ThemedText>
          <SharingControl
            sourceId={place.id}
            sourceType="place"
            shares={shares}
            isLoading={isLoadingShares}
            canShare={place.canShare}
            onShare={onShare}
            onUnshare={onUnshare}
          />
        </ThemedView>
      )}

      {children}

      {/* Footer Controls */}
      <View style={styles.footer}>
        <View style={styles.leftFooterControls}>
          <ThemedButton
            title={isExisting || !canEdit ? "Back" : "Cancel"}
            variant="minimal"
            onPress={onCancel}
          />
          {isExisting && (
            <AutoSaveIndicator
              status={autoSave.status}
              errorMessage={autoSave.error}
            />
          )}
        </View>

        <View style={styles.rightFooterControls}>
          {!isExisting && canEdit && (
            <ThemedButton
              title={isSaving ? "Creating..." : (submitTitle || "Create Place")}
              onPress={handleManualSave}
              disabled={isSaving}
              loading={isSaving}
              style={styles.actionBtn}
              testID="place-submit-button"
            />
          )}
        </View>
      </View>

      {/* Map Location Selector Modal */}
      {showLocationPicker && (
        <GoogleMapsLocationSelector
          visible={showLocationPicker}
          onClose={() => setShowLocationPicker(false)}
          initialLocation={formAddress}
          initialLatitude={formLat}
          initialLongitude={formLng}
          onSelect={(result) => {
            setFormAddress(result.location);
            setFormLat(result.latitude);
            setFormLng(result.longitude);
            setShowLocationPicker(false);
          }}
        />
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 40,
  },
  section: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.05)",
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    marginBottom: 16,
    gap: 12,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
  },
  addressRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 10,
  },
  mapPickerButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 2,
    gap: 6,
  },
  mapPickerText: {
    fontSize: 13,
    fontWeight: "600",
  },
  coordinatesBadge: {
    fontSize: 12,
    marginTop: -6,
  },
  fieldBlock: {
    gap: 6,
  },
  fieldLabel: {
    fontSize: 13,
  },
  footer: {
    marginTop: 8,
    paddingBottom: 24,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  leftFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  rightFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  actionBtn: {
    minWidth: 140,
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    marginTop: 12,
    marginBottom: 6,
    gap: 12,
  },
  ratingLabel: {
    marginBottom: 0,
  },
  ratingControl: {
    marginVertical: 0,
  },
});
